import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ThumbsUp, Copy, ArrowUpRight, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { copyText, targetName, targetPath } from "@/lib/catalogue";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const PromptCard = ({ prompt, onChanged, onEdit, compact = false }) => {
  const { user } = useAuth();
  const { agentsByKey, workflowsByKey } = useCatalogue();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const retired = prompt.status === "retired";
  const [retiring, setRetiring] = useState(false);
  const [reason, setReason] = useState("");
  const retire = async () => { await act(() => api.patch(`/prompts/${prompt.id}`, { status: "retired", retired_reason: reason })); setRetiring(false); setReason(""); };

  const copy = async (thenOpen = false) => {
    const ok = await copyText(prompt.text);
    if (!ok) return toast.error("Could not copy. Select the text manually.");
    try { const { data } = await api.post(`/prompts/${prompt.id}/copy`); onChanged(data); } catch { /* count is best effort */ }
    toast.success("Prompt copied");
    if (thenOpen && prompt.for_id !== "any") navigate(targetPath(prompt.for_id, workflowsByKey));
  };
  const act = async (fn) => { try { const { data } = await fn(); onChanged(data); } catch (e) { toast.error(errorMessage(e)); } };

  return (
    <article className={cn("card-elevated p-5", retired && "opacity-70")} data-testid={`prompt-card-${prompt.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{prompt.title}{retired && <span className="ml-2 text-xs font-normal text-muted-foreground">Retired</span>}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            For {targetName(prompt.for_id, agentsByKey, workflowsByKey)} · {prompt.author_name} · {formatDate(prompt.created_at)}
            {prompt.updated_at !== prompt.created_at && ` · updated ${formatDate(prompt.updated_at)}`}
            {prompt.rating && ` · result ${prompt.rating} of 5`}
          </p>
        </div>
        {prompt.can_edit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label={`Actions for ${prompt.title}`} data-testid={`prompt-actions-${prompt.id}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem onSelect={() => onEdit(prompt)} data-testid="prompt-action-edit">Edit</DropdownMenuItem>
              {!retired && <DropdownMenuItem onSelect={() => setRetiring(true)} data-testid="prompt-action-retire">Retire</DropdownMenuItem>}
              {retired && user.role === "owner" && <DropdownMenuItem onSelect={() => act(() => api.patch(`/prompts/${prompt.id}`, { status: "active" }))} data-testid="prompt-action-restore">Restore</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {prompt.channels?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">{prompt.channels.map((c) => <span key={c} className="rounded-full border px-2 py-0.5 text-xs">{c}</span>)}</div>
      )}
      <p className={cn("mt-3 whitespace-pre-wrap text-sm", !open && (compact ? "line-clamp-3" : "line-clamp-5"))} data-testid="prompt-text">{prompt.text}</p>
      {prompt.text.length > 240 && <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 text-xs font-medium underline underline-offset-4" data-testid="prompt-expand">{open ? "Show less" : "Show all"}</button>}
      {prompt.example_output && (
        <details className="mt-3 rounded-lg border px-3 py-2" data-testid={`prompt-example-${prompt.id}`}>
          <summary className="cursor-pointer text-sm font-medium">Example output</summary>
          <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{prompt.example_output}</p>
        </details>
      )}
      {prompt.why_it_worked && <p className="mt-3 rounded-lg bg-muted px-3 py-2 text-sm"><span className="font-medium">Why it worked: </span>{prompt.why_it_worked}</p>}
      {prompt.campaign && <p className="mt-2 text-xs text-muted-foreground">Campaign: {prompt.campaign}</p>}
      {retired && <p className="mt-2 text-sm text-muted-foreground" data-testid={`prompt-retired-note-${prompt.id}`}>Retired{prompt.retired_by ? ` by ${prompt.retired_by}` : ""}{prompt.retired_at ? ` on ${formatDate(prompt.retired_at)}` : ""}{prompt.retired_reason ? `. Reason: ${prompt.retired_reason}` : ""}. Only you and Owners can see it.</p>}
      <Dialog open={retiring} onOpenChange={setRetiring}>
        <DialogContent className="rounded-xl" data-testid="prompt-retire-dialog">
          <DialogHeader><DialogTitle>Retire this prompt</DialogTitle><DialogDescription>Retired prompts are hidden from everyone except you and Owners. An Owner can restore it.</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label htmlFor={`retire-reason-${prompt.id}`}>Reason (optional)</Label><Input id={`retire-reason-${prompt.id}`} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} data-testid="prompt-retire-reason" /></div>
          <DialogFooter><Button variant="ghost" onClick={() => setRetiring(false)}>Cancel</Button><Button onClick={retire} data-testid="prompt-retire-confirm">Retire prompt</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant={prompt.has_upvoted ? "default" : "secondary"} size="sm" aria-pressed={prompt.has_upvoted} disabled={user.role === "viewer"}
          onClick={() => act(() => api.post(`/prompts/${prompt.id}/upvote`))} data-testid={`prompt-upvote-${prompt.id}`}>
          <ThumbsUp className="!h-4 !w-4" /> {prompt.upvotes} <span className="sr-only">upvotes</span>
        </Button>
        <Button variant="secondary" size="sm" onClick={() => copy(false)} data-testid={`prompt-copy-${prompt.id}`}><Copy className="!h-4 !w-4" /> Copy</Button>
        {prompt.for_id !== "any" && <Button size="sm" onClick={() => copy(true)} data-testid={`prompt-use-${prompt.id}`}><ArrowUpRight className="!h-4 !w-4" /> Use this prompt</Button>}
        <span className="ml-auto text-xs text-muted-foreground" data-testid={`prompt-uses-${prompt.id}`}>{prompt.copies} {prompt.copies === 1 ? "use" : "uses"}</span>
      </div>
    </article>
  );
};
